import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Badge, Button, Form, Dropdown, Modal } from "react-bootstrap";
import { baseURL } from "@helper/interceptor";
import { getAllUsers } from "@modules/Users/services/userCrud";
import {
  ClipboardIcon,
  EditIcon,
  MoistureIcon,
  TaskIcon,
  WrenchIcon,
  MoreIcon,
  FilterIconSolid,
  SortDownIcon,
  SortUpIcon,
} from "@components/icons";
import { Scissors } from "@styled-icons/bootstrap/Scissors";
import { Trash } from "@styled-icons/bootstrap/Trash";
import { Star } from "@styled-icons/bootstrap/Star";
import { PencilSquare } from "@styled-icons/bootstrap/PencilSquare";
import { Check } from "@styled-icons/bootstrap/Check";
import { FileText } from "@styled-icons/bootstrap/FileText";
import { Image } from "@styled-icons/bootstrap/Image";
import { REGELKONTROLLE_GROUPS } from "../constants/sichtkontrolle";
import TaskDetail from "@modules/Tasks/components/TaskDetail";

function toLocalDatetimeValue(value) {
  const d = value ? new Date(value) : new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

const TASK_STATUSES = ["GEPLANT", "BEAUFTRAGT", "IN_ARBEIT"];
const HISTORY_STATUSES = ["ERLEDIGT", "NICHT_GESCHAFFT"];
const ALL_STATUSES = [...TASK_STATUSES, ...HISTORY_STATUSES];
const IRRIGATION_TYPE = "Bewässerung";
const LEGACY_IRRIGATION_TYPE = "Gießmaßnahme";
const PRUNING_TYPE = "Schnittmaßnahme";
const IRRIGATION_WEATHER_OPTIONS = [
  { value: "sonnig", labelKey: "care_chat.weather_sunny" },
  { value: "bewoelkt", labelKey: "care_chat.weather_cloudy" },
  { value: "regen vorher", labelKey: "care_chat.weather_rain_before" },
  { value: "regen nachher", labelKey: "care_chat.weather_rain_after" },
  { value: "windig", labelKey: "care_chat.weather_windy" },
  { value: "trocken", labelKey: "care_chat.weather_dry" },
];
const IRRIGATION_SOIL_MOISTURE_OPTIONS = [
  { value: "trocken", labelKey: "care_chat.soil_dry" },
  { value: "leicht feucht", labelKey: "care_chat.soil_slightly_moist" },
  { value: "nass", labelKey: "care_chat.soil_wet" },
];
const IRRIGATION_TREE_CONDITION_OPTIONS = [
  { value: "gesund", labelKey: "care_chat.tree_condition_healthy" },
  { value: "blaetter welk", labelKey: "care_chat.tree_condition_wilted_leaves" },
  { value: "schaeden", labelKey: "care_chat.tree_condition_damage" },
  { value: "neigung", labelKey: "care_chat.tree_condition_tilt" },
  { value: "sonstiges", labelKey: "care_chat.tree_condition_other" },
];
const PRUNING_CATEGORIES = [
  { value: "Erziehungsschnitt", labelKey: "care_chat.pruning_formative" },
  { value: "Pflegeschnitt", labelKey: "care_chat.pruning_maintenance" },
  { value: "Kroneneinkuerzung / Kronenpflege", labelKey: "care_chat.pruning_crown_reduction" },
  { value: "Freistellen / Freischnitt", labelKey: "care_chat.pruning_clearing" },
  {
    value: "Sanierungsschnitt (Erhaltungsschnitt / Korrekturschnitt)",
    labelKey: "care_chat.pruning_remedial",
  },
  {
    value: "Sicherheitsschnitt (Verkehrssicherheitsschnitt)",
    labelKey: "care_chat.pruning_safety",
  },
];

const STATUS_LABEL_KEYS = {
  GEPLANT: "care_chat.status_planned",
  BEAUFTRAGT: "care_chat.status_commissioned",
  IN_ARBEIT: "care_chat.status_in_progress",
  ERLEDIGT: "care_chat.status_done",
  NICHT_GESCHAFFT: "care_chat.status_not_done",
  planned: "care_chat.status_planned",
  review: "care_chat.status_commissioned",
  "in-progress": "care_chat.status_in_progress",
  completed: "care_chat.status_done",
};

const PRIORITY_LABEL_KEYS = {
  NIEDRIG: "care_chat.priority_low",
  MITTEL: "care_chat.priority_medium",
  HOCH: "care_chat.priority_high",
  KRITISCH: "care_chat.priority_critical",
  low: "care_chat.priority_low",
  medium: "care_chat.priority_medium",
  high: "care_chat.priority_high",
};

const THEME = {
  primary: "var(--color-action, #286CA5)",
  primaryDark: "var(--color-text-brand, #08424e)",
  primaryLight: "var(--color-action-subtle, #d4e6e7)",
  border: "var(--color-border-default, #e5e7eb)",
  borderLight: "var(--color-border-subtle, #f3f4f6)",
  background: "var(--color-bg-subtle, #fafafa)",
  surface: "var(--color-bg-surface, #ffffff)",
  text: "var(--color-text-primary, #495057)",
  muted: "var(--color-text-secondary, #6c757d)",
  danger: "#dc3545",
  dangerLight: "var(--color-error-subtle, #f8d7da)",
  success: "#198754",
  successLight: "var(--color-success-subtle, #d1e7dd)",
  warning: "#ffc107",
  warningLight: "var(--color-warning-subtle, #fff3cd)",
  chip: "var(--color-bg-subtle, #f8f9fa)",
};

const FILTER_BAR_STYLE = {
  background: THEME.chip,
  border: `1px solid ${THEME.border}`,
  borderRadius: "10px",
  padding: "8px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
};

const CONTROL_BAR_STYLE = {
  background: THEME.surface,
  border: `1px solid ${THEME.border}`,
  borderRadius: "12px",
  padding: "10px 12px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  borderLeft: `4px solid ${THEME.primary}`,
};

const SELECT_STYLE = {
  borderColor: THEME.border,
  borderRadius: "8px",
  background: THEME.surface,
  minWidth: "200px",
  flex: "1 1 0",
};

const TOOL_ICON_BUTTON_STYLE = {
  borderColor: THEME.primary,
  color: THEME.primary,
  width: "30px",
  height: "30px",
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

function getToolIconButtonStyle(active = false) {
  return {
    ...TOOL_ICON_BUTTON_STYLE,
    background: active ? THEME.primaryLight : THEME.surface,
    borderColor: active ? THEME.primary : TOOL_ICON_BUTTON_STYLE.borderColor,
  };
}

const INPUT_STYLE = {
  borderColor: THEME.border,
  borderRadius: "8px",
  background: THEME.surface,
  color: THEME.text,
};

const PILL_STYLE = {
  borderRadius: "6px",
  padding: "0.25rem 0.6rem",
  fontSize: "0.75rem",
  fontWeight: 500,
  border: "1px solid transparent",
};

const MEASURE_ICONS = {
  [IRRIGATION_TYPE]: MoistureIcon,
  [LEGACY_IRRIGATION_TYPE]: MoistureIcon,
  [PRUNING_TYPE]: Scissors,
  Regelkontrolle: ClipboardIcon,
  "Freie Eintragung": FileText,
  "Sensor am Baum montieren": WrenchIcon,
  "Sensorwartung am Baum durchfuehren": WrenchIcon,
  Baumfoto: Image,
  Blattfoto: Image,
};

const MEASURE_TYPE_LABEL_KEYS = {
  [IRRIGATION_TYPE]: "care_chat.measure_irrigation",
  [LEGACY_IRRIGATION_TYPE]: "care_chat.measure_irrigation",
  [PRUNING_TYPE]: "care_chat.measure_pruning",
  Regelkontrolle: "care_chat.measure_inspection",
  "Freie Eintragung": "care_chat.measure_free",
};

function getStatusBadgeStyle(status) {
  const normalized =
    status === "completed" ? "ERLEDIGT" : status === "not_completed" ? "NICHT_GESCHAFFT" : status;
  if (normalized === "NICHT_GESCHAFFT") {
    return {
      ...PILL_STYLE,
      backgroundColor: THEME.danger,
      color: "#ffffff",
      border: `1px solid ${THEME.danger}`,
    };
  }
  if (normalized === "ERLEDIGT") {
    return {
      ...PILL_STYLE,
      backgroundColor: THEME.success,
      color: "#ffffff",
      border: `1px solid ${THEME.success}`,
    };
  }
  return {
    ...PILL_STYLE,
    backgroundColor: "#0d6efd",
    color: "#ffffff",
    border: "1px solid #0b5ed7",
  };
}

function getStatusOptionsForTimestamp(timestampLocal, now) {
  if (!timestampLocal) return ALL_STATUSES;
  const date = new Date(timestampLocal);
  return date > now ? TASK_STATUSES : HISTORY_STATUSES;
}

function getPriorityBadgeStyle(priority) {
  const normalized =
    priority === "high"
      ? "HOCH"
      : priority === "medium"
      ? "MITTEL"
      : priority === "low"
      ? "NIEDRIG"
      : priority;
  if (normalized === "KRITISCH") {
    return {
      ...PILL_STYLE,
      backgroundColor: THEME.danger,
      color: "#ffffff",
      border: `1px solid ${THEME.danger}`,
    };
  }
  if (normalized === "HOCH") {
    return {
      ...PILL_STYLE,
      backgroundColor: "#fd7e14",
      color: "#ffffff",
      border: "1px solid #f76707",
    };
  }
  if (normalized === "MITTEL") {
    return {
      ...PILL_STYLE,
      backgroundColor: "#0d6efd",
      color: "#ffffff",
      border: "1px solid #0b5ed7",
    };
  }
  return {
    ...PILL_STYLE,
    backgroundColor: "#6c757d",
    color: "#ffffff",
    border: "1px solid #5c636a",
  };
}

const TAG_BADGE_STYLE = {
  backgroundColor: "#e9ecef",
  color: THEME.text,
  border: "1px solid #dee2e6",
  ...PILL_STYLE,
};

const META_BADGE_STYLE = {
  backgroundColor: "#0d6efd",
  color: "#ffffff",
  border: "1px solid #0b5ed7",
  ...PILL_STYLE,
};

const TERM_BADGE_STYLE = {
  backgroundColor: "#0d6efd",
  color: "#ffffff",
  border: "1px solid #0b5ed7",
  ...PILL_STYLE,
};

const PRIORITY_ORDER = {
  KRITISCH: 4,
  HOCH: 3,
  MITTEL: 2,
  NIEDRIG: 1,
};
const PRIORITY_FILTER_VALUES = ["KRITISCH", "HOCH", "MITTEL", "NIEDRIG"];

function normalizePriority(priority) {
  if (priority === "high") return "HOCH";
  if (priority === "medium") return "MITTEL";
  if (priority === "low") return "NIEDRIG";
  return priority || "";
}

function normalizeStatus(status) {
  if (status === "planned") return "GEPLANT";
  if (status === "review") return "BEAUFTRAGT";
  if (status === "in-progress") return "IN_ARBEIT";
  if (status === "completed" || status === "done") return "ERLEDIGT";
  if (status === "not_completed") return "NICHT_GESCHAFFT";
  return status || "";
}

function normalizeMeasureType(type) {
  const value = String(type || "").trim();
  if (value === LEGACY_IRRIGATION_TYPE) return IRRIGATION_TYPE;
  return value;
}

function isIrrigationMeasureType(type) {
  const value = normalizeMeasureType(type);
  return value === IRRIGATION_TYPE;
}

function getTimestampMs(value) {
  const ts = new Date(value || 0).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

function resolveDocumentUrl(fileUrl) {
  if (!fileUrl) return "";
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  try {
    const normalizedPath = fileUrl.startsWith("/") ? fileUrl : `/${fileUrl}`;
    return new URL(normalizedPath, baseURL || window.location.origin).toString();
  } catch (error) {
    return fileUrl;
  }
}

function buildChecklistDraft(existing) {
  const next = {};
  REGELKONTROLLE_GROUPS.forEach((group) => {
    group.items.forEach((item) => {
      next[item.id] = Boolean(existing?.[item.id]);
    });
  });
  return next;
}

const DEFAULT_INTERVAL_YEARS = 3;

function getCareYear(plantingYear) {
  const year = Number(plantingYear);
  if (!year || Number.isNaN(year)) return "";
  const diff = new Date().getFullYear() - year + 1;
  if (diff <= 1) return "1";
  if (diff >= 3) return "3";
  return String(diff);
}

function buildRegelkontrolleMeta(existing, entry) {
  return {
    kontrolliertVon: existing?.kontrolliertVon || entry?.author || "Du",
    kontrolliertAmLocal: existing?.kontrolliertAm
      ? toLocalDatetimeValue(existing.kontrolliertAm)
      : toLocalDatetimeValue(entry?.timestamp || new Date()),
    intervallJahre:
      typeof existing?.intervallJahre === "number"
        ? existing.intervallJahre
        : DEFAULT_INTERVAL_YEARS,
    verkehrssicher: typeof existing?.verkehrssicher === "boolean" ? existing.verkehrssicher : true,
    bemerkung: existing?.bemerkung || "",
  };
}

function buildIrrigationDraft(entry, tree) {
  const log = entry?.data?.irrigationLog || {};
  const timestampLocal = toLocalDatetimeValue(entry?.timestamp);
  const projectName = tree?.area?.project?.name;
  const areaName = tree?.area?.name;
  const location = [projectName, areaName].filter(Boolean).join(" / ");
  const treeSpecies =
    tree?.species?.name || tree?.species?.de_name || tree?.species || tree?.name || "";
  return {
    projectLocation: log.projectLocation || location || "",
    treeSpecies: log.treeSpecies || treeSpecies || "",
    careYear: log.careYear || (tree?.planting_year ? getCareYear(tree.planting_year) : ""),
    date: log.date || timestampLocal.slice(0, 10),
    time: log.time || timestampLocal.slice(11, 16),
    waterLiters:
      log.waterLiters != null
        ? log.waterLiters
        : entry?.data?.mengeLiter != null
        ? entry.data.mengeLiter
        : "",
    method: log.method || entry?.data?.methode || "",
    reason: log.reason || entry?.data?.grund || "",
    weather: log.weather || "",
    soilMoisture: log.soilMoisture || "",
    treeCondition: log.treeCondition || "",
    responsiblePerson: log.responsiblePerson || entry?.assignedTo || "",
    notes: log.notes || "",
  };
}

function buildPruningDraft(entry) {
  const log = entry?.data?.pruningLog || {};
  const timestampLocal = toLocalDatetimeValue(entry?.timestamp);
  return {
    type: log.type || "",
    treeCondition: log.treeCondition || "",
    notes: log.notes || "",
    responsiblePerson: log.responsiblePerson || entry?.assignedTo || "",
    date: log.date || timestampLocal.slice(0, 10),
    time: log.time || timestampLocal.slice(11, 16),
  };
}

function getNextControlLocal(kontrolliertAmLocal, intervallJahre) {
  if (!kontrolliertAmLocal) return "";
  const base = new Date(kontrolliertAmLocal);
  if (Number.isNaN(base.getTime())) return "";
  const next = new Date(base);
  next.setFullYear(next.getFullYear() + Number(intervallJahre || 0));
  return toLocalDatetimeValue(next);
}

function toIsoFromLocal(localValue) {
  if (!localValue) return null;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function getNextControlIso(kontrolliertAmLocal, intervallJahre) {
  const iso = toIsoFromLocal(kontrolliertAmLocal);
  if (!iso) return null;
  const next = new Date(iso);
  next.setFullYear(next.getFullYear() + Number(intervallJahre || 0));
  return next.toISOString();
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function RegelkontrolleChecklist({ checks, onToggle }) {
  const isEditable = typeof onToggle === "function";
  const [openGroups, setOpenGroups] = useState({});
  const groupsToRender = isEditable
    ? REGELKONTROLLE_GROUPS
    : REGELKONTROLLE_GROUPS.filter((group) => group.items.some((item) => checks?.[item.id]));
  return (
    <div className="d-grid gap-2">
      {groupsToRender.map((group) => {
        const checkedCount = group.items.filter((item) => checks?.[item.id]).length;
        const summaryCount = isEditable
          ? checkedCount
            ? ` (${checkedCount}/${group.items.length})`
            : ""
          : checkedCount
          ? ` (${checkedCount})`
          : "";
        const visibleItems = isEditable
          ? group.items
          : group.items.filter((item) => checks?.[item.id]);
        return (
          <details
            key={group.id}
            open={Boolean(openGroups[group.id])}
            onToggle={(event) => {
              const isOpen = event?.currentTarget?.open;
              setOpenGroups((prev) => ({
                ...prev,
                [group.id]: typeof isOpen === "boolean" ? isOpen : !prev[group.id],
              }));
            }}
            style={{
              border: `1px solid ${THEME.border}`,
              borderRadius: "8px",
              background: THEME.surface,
              padding: "6px 10px",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                color: THEME.text,
                fontWeight: 600,
                listStyle: "none",
              }}
            >
              {group.label}
              {summaryCount}
            </summary>
            <div className="d-grid gap-1 mt-2">
              {visibleItems.map((item) =>
                isEditable ? (
                  <label
                    key={item.id}
                    className="d-flex align-items-start gap-2 small"
                    style={{ color: THEME.text }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(checks?.[item.id])}
                      onChange={() => onToggle(item.id)}
                      className="tree-care-checkbox"
                    />
                    <span>{item.label}</span>
                  </label>
                ) : (
                  <div
                    key={item.id}
                    className="small d-flex align-items-start gap-2"
                    style={{ color: THEME.text }}
                  >
                    <span>-</span>
                    <span>{item.label}</span>
                  </div>
                )
              )}
            </div>
          </details>
        );
      })}
    </div>
  );
}

function ChatBubble({
  entry,
  align = "start",
  highlight = false,
  footer,
  anchorId,
  onDelete,
  onEdit,
  onToggleStatus,
  onToggleFavorite,
  isHistory = false,
  isExpanded = false,
  onToggleExpand,
  onImageClick,
  onTaskClick,
}) {
  const { t } = useTranslation();
  const bg = highlight ? THEME.dangerLight : THEME.surface;
  const border = highlight ? THEME.danger : THEME.border;
  const normalizedMeasureType = normalizeMeasureType(entry.measureType);
  const MeasureIcon =
    MEASURE_ICONS[normalizedMeasureType] || MEASURE_ICONS[entry.measureType] || TaskIcon;
  const measureLabelKey = MEASURE_TYPE_LABEL_KEYS[normalizedMeasureType];
  const displayMeasureType = measureLabelKey
    ? t(measureLabelKey)
    : normalizedMeasureType || entry.measureType;
  const hasChecklist = entry.measureType === "Regelkontrolle" && entry.data?.sichtkontrolleChecks;
  const checklistMeta = entry.data?.sichtkontrolleMeta;
  const irrigationLog = entry.data?.irrigationLog;
  const pruningLog = entry.data?.pruningLog;
  const regelkontrolleDocument = entry.data?.regelkontrolleDocument;
  const regelkontrolleDocumentUrl = resolveDocumentUrl(regelkontrolleDocument?.file);
  const freieEintragungDocument = entry.data?.freieEintragungDocument;
  const freieEintragungDocumentUrl = resolveDocumentUrl(freieEintragungDocument?.file);
  const description = entry.data?.beschreibung || "";
  const hasMetaDetails =
    (entry.data?.mengeLiter != null && entry.data?.mengeLiter !== "") ||
    Boolean(entry.data?.methode) ||
    Boolean(entry.data?.schnittArt) ||
    Boolean(entry.data?.umfang) ||
    Boolean(entry.data?.kontrollstufe) ||
    Boolean(entry.data?.befund) ||
    Boolean(entry.data?.empfehlung) ||
    Boolean(entry.data?.grund) ||
    Boolean(entry.data?.ausfuehrende) ||
    Boolean(entry.data?.frist) ||
    Boolean(entry.costCenter) ||
    Boolean(entry.assignedTo);
  const hasExtendedIrrigationFields =
    Boolean(irrigationLog?.weather) ||
    Boolean(irrigationLog?.soilMoisture) ||
    Boolean(irrigationLog?.treeCondition) ||
    Boolean(irrigationLog?.responsiblePerson) ||
    Boolean(irrigationLog?.notes);
  const hasExtendedPruningFields = Boolean(pruningLog?.notes);
  const showExpandToggle =
    isHistory && (Boolean(description) || hasExtendedIrrigationFields || hasExtendedPruningFields);
  const showExtendedIrrigation = !isHistory || isExpanded;
  const showExtendedPruning = !isHistory || isExpanded;

  const isTaskEntry =
    entry.source === "task" || entry.entryKind === "task" || entry.sourceId != null;

  return (
    <div id={anchorId} className={`d-flex flex-column align-items-${align} mb-3`}>
      <div
        className="p-3 rounded-3 border position-relative"
        style={{
          background: bg,
          borderColor: border,
          maxWidth: "520px",
          color: THEME.text,
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.04)",
          cursor: isTaskEntry ? "pointer" : "default",
        }}
        onClick={(e) => {
          if (isTaskEntry) {
            e.stopPropagation();
            onTaskClick?.(entry);
          }
        }}
      >
        {/* Three dots menu */}
        <div
          style={{ position: "absolute", top: "12px", right: "12px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="link"
              style={{
                padding: "4px",
                border: "none",
                background: "transparent",
                boxShadow: "none",
              }}
              bsPrefix="custom-dropdown"
            >
              <MoreIcon size="20px" color={THEME.muted} />
            </Dropdown.Toggle>

            <Dropdown.Menu
              style={{
                borderRadius: "8px",
                border: `1px solid ${THEME.border}`,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "0.5rem 0",
                minWidth: "180px",
              }}
            >
              {entry.future && (
                <>
                  <Dropdown.Item
                    onClick={() => onEdit?.(entry.id)}
                    style={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      color: THEME.text,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <PencilSquare size={16} />
                    {t("care_chat.edit")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => onToggleStatus?.(entry)}
                    style={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      color: THEME.text,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Check size={16} />
                    {entry.status === "ERLEDIGT"
                      ? t("care_chat.mark_not_done")
                      : t("care_chat.mark_done")}
                  </Dropdown.Item>
                  <Dropdown.Divider style={{ margin: "0.5rem 0" }} />
                </>
              )}
              <Dropdown.Item
                onClick={() => onToggleFavorite?.(entry.id)}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.875rem",
                  color: THEME.text,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Star size={16} />
                {t("care_chat.favorite")}
              </Dropdown.Item>
              <Dropdown.Divider style={{ margin: "0.5rem 0" }} />
              <Dropdown.Item
                onClick={() => onDelete?.(entry.id)}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.875rem",
                  color: THEME.danger,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Trash size={16} />
                {t("care_chat.delete")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {entry.source === "image" ? (
          // Image entry rendering
          <div>
            <div className="d-flex align-items-start gap-2 mb-2" style={{ paddingRight: "30px" }}>
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  border: `1px solid ${THEME.borderLight}`,
                  background: THEME.chip,
                }}
              >
                <MeasureIcon size="16px" color={THEME.primary} style={{ marginRight: 0 }} />
              </div>
              <div className="fw-semibold" style={{ color: THEME.primary }}>
                {displayMeasureType}
              </div>
            </div>
            <img
              src={entry.imageUrl}
              alt={displayMeasureType}
              style={{
                width: "100%",
                maxWidth: "460px",
                height: "auto",
                borderRadius: "8px",
                cursor: "pointer",
                objectFit: "cover",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onImageClick?.(entry.imageUrl, displayMeasureType);
              }}
            />
          </div>
        ) : (
          // Regular entry rendering
          <div className="d-flex align-items-start gap-2" style={{ paddingRight: "30px" }}>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                border: `1px solid ${THEME.borderLight}`,
                background: THEME.chip,
              }}
            >
              <MeasureIcon size="16px" color={THEME.primary} style={{ marginRight: 0 }} />
            </div>
            <div className="fw-semibold" style={{ color: THEME.primary }}>
              {displayMeasureType}
              {entry.data?.beschreibung ? `: ${entry.data.beschreibung}` : ""}
            </div>
          </div>
        )}

        {hasMetaDetails && (
          <div
            className="small mt-2 rounded-3 p-2"
            style={{
              color: THEME.muted,
              background: THEME.chip,
              border: `1px solid ${THEME.border}`,
            }}
          >
            {!irrigationLog && entry.data?.mengeLiter != null && entry.data?.mengeLiter !== "" && (
              <div>
                {t("care_chat.amount")} {entry.data.mengeLiter} L
              </div>
            )}
            {!irrigationLog && entry.data?.methode && (
              <div>
                {t("care_chat.method")} {entry.data.methode}
              </div>
            )}

            {entry.data?.schnittArt && (
              <div>
                {t("care_chat.cut_type")} {entry.data.schnittArt}
              </div>
            )}
            {entry.data?.umfang && (
              <div>
                {t("care_chat.scope")} {entry.data.umfang}
              </div>
            )}

            {entry.data?.kontrollstufe && (
              <div>
                {t("care_chat.inspection_level")} {entry.data.kontrollstufe}
              </div>
            )}
            {entry.data?.befund && (
              <div>
                {t("care_chat.finding")} {entry.data.befund}
              </div>
            )}
            {entry.data?.empfehlung && (
              <div>
                {t("care_chat.recommendation")} {entry.data.empfehlung}
              </div>
            )}

            {!irrigationLog && entry.data?.grund && (
              <div>
                {t("care_chat.reason_display")} {entry.data.grund}
              </div>
            )}
            {!irrigationLog && entry.data?.ausfuehrende && (
              <div>
                {t("care_chat.executor")} {entry.data.ausfuehrende}
              </div>
            )}
            {entry.data?.frist && (
              <div>
                {t("care_chat.deadline")} {entry.data.frist}
              </div>
            )}

            {entry.costCenter && (
              <div>
                {t("care_chat.cost_center_display")} {entry.costCenter}
              </div>
            )}
            {entry.assignedTo && (
              <div>
                {t("care_chat.assignment_display")} {entry.assignedTo}
              </div>
            )}
          </div>
        )}

        {isHistory && isExpanded && description && (
          <div
            className="small mt-2 rounded-3 p-2"
            style={{
              color: THEME.muted,
              background: THEME.chip,
              border: `1px solid ${THEME.border}`,
              whiteSpace: "pre-wrap",
            }}
          >
            <div>
              {t("care_chat.description_display")} {description}
            </div>
          </div>
        )}

        {irrigationLog && (
          <div
            className="small mt-2 rounded-3 p-2"
            style={{
              color: THEME.muted,
              background: THEME.chip,
              border: `1px solid ${THEME.border}`,
            }}
          >
            <div className="fw-semibold mb-1" style={{ color: THEME.text }}>
              {t("care_chat.irrigation_log_title")}
            </div>
            {(irrigationLog.date || irrigationLog.time) && (
              <div>
                {t("care_chat.datetime_display")}{" "}
                {[irrigationLog.date, irrigationLog.time].filter(Boolean).join(" ")}
              </div>
            )}
            {irrigationLog.waterLiters != null && irrigationLog.waterLiters !== "" && (
              <div>
                {t("care_chat.water_amount_display")} {irrigationLog.waterLiters} L
              </div>
            )}
            {irrigationLog.method && (
              <div>
                {t("care_chat.irrigation_method_display")} {irrigationLog.method}
              </div>
            )}
            {irrigationLog.reason && (
              <div>
                {t("care_chat.reason_label")} {irrigationLog.reason}
              </div>
            )}
            {showExtendedIrrigation && (
              <>
                {irrigationLog.weather && (
                  <div>
                    {t("care_chat.weather_display")} {irrigationLog.weather}
                  </div>
                )}
                {irrigationLog.soilMoisture && (
                  <div>
                    {t("care_chat.soil_moisture_display")} {irrigationLog.soilMoisture}
                  </div>
                )}
                {irrigationLog.treeCondition && (
                  <div>
                    {t("care_chat.tree_condition_display")} {irrigationLog.treeCondition}
                  </div>
                )}
                {irrigationLog.responsiblePerson && (
                  <div>
                    {t("care_chat.responsible_person_display")} {irrigationLog.responsiblePerson}
                  </div>
                )}
                {irrigationLog.notes && (
                  <div>
                    {t("care_chat.remarks_display")} {irrigationLog.notes}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {pruningLog && (
          <div
            className="small mt-2 rounded-3 p-2"
            style={{
              color: THEME.muted,
              background: THEME.chip,
              border: `1px solid ${THEME.border}`,
            }}
          >
            <div className="fw-semibold mb-1" style={{ color: THEME.text }}>
              {t("care_chat.pruning_log_title")}
            </div>
            {(pruningLog.date || pruningLog.time) && (
              <div>
                {t("care_chat.datetime_display")}{" "}
                {[pruningLog.date, pruningLog.time].filter(Boolean).join(" ")}
              </div>
            )}
            {pruningLog.type && (
              <div>
                {t("care_chat.pruning_type_display")} {pruningLog.type}
              </div>
            )}
            {pruningLog.treeCondition && (
              <div>
                {t("care_chat.tree_condition_display")} {pruningLog.treeCondition}
              </div>
            )}
            {pruningLog.responsiblePerson && (
              <div>
                {t("care_chat.responsible_person_display")} {pruningLog.responsiblePerson}
              </div>
            )}
            {showExtendedPruning && pruningLog.notes && (
              <div>
                {t("care_chat.remarks_display")} {pruningLog.notes}
              </div>
            )}
          </div>
        )}

        {hasChecklist && (
          <div className="mt-2">
            <RegelkontrolleChecklist checks={entry.data?.sichtkontrolleChecks} />
          </div>
        )}

        {checklistMeta && (
          <div
            className="small mt-2 rounded-3 p-2"
            style={{
              color: THEME.muted,
              background: THEME.chip,
              border: `1px solid ${THEME.border}`,
            }}
          >
            {checklistMeta.kontrolliertVon && (
              <div>
                {t("care_chat.inspected_by_display")} {checklistMeta.kontrolliertVon}
              </div>
            )}
            {checklistMeta.kontrolliertAm && (
              <div>
                {t("care_chat.inspection_date_display")}{" "}
                {formatDateTime(checklistMeta.kontrolliertAm)}
              </div>
            )}
            {checklistMeta.naechsteKontrolle && (
              <div>
                {t("care_chat.next_inspection_display")}{" "}
                {formatDateTime(checklistMeta.naechsteKontrolle)}
              </div>
            )}
            {checklistMeta.intervallJahre != null && (
              <div>
                {t("care_chat.interval_display")} {checklistMeta.intervallJahre}
              </div>
            )}
            {typeof checklistMeta.verkehrssicher === "boolean" && (
              <div>
                {t("care_chat.road_safe_display")}{" "}
                {checklistMeta.verkehrssicher ? t("care_chat.yes") : t("care_chat.no")}
              </div>
            )}
            {checklistMeta.bemerkung && (
              <div>
                {t("care_chat.remark_display")} {checklistMeta.bemerkung}
              </div>
            )}
          </div>
        )}

        {regelkontrolleDocument && (
          <div className="mt-2 small">
            {regelkontrolleDocumentUrl ? (
              <a
                href={regelkontrolleDocumentUrl}
                target="_blank"
                rel="noreferrer"
                download={regelkontrolleDocument.name || true}
                className="d-inline-flex align-items-center gap-1"
                style={{ color: THEME.primary }}
                onClick={(e) => e.stopPropagation()}
              >
                <FileText size={14} />
                {t("care_chat.attachment_display")} {regelkontrolleDocument.name || "Dokument"}
              </a>
            ) : (
              <div
                className="d-inline-flex align-items-center gap-1"
                style={{ color: THEME.muted }}
              >
                <FileText size={14} />
                {t("care_chat.attachment_display")} {regelkontrolleDocument.name || "Dokument"}
              </div>
            )}
          </div>
        )}

        {freieEintragungDocument && (
          <div className="mt-2 small">
            {freieEintragungDocumentUrl ? (
              <a
                href={freieEintragungDocumentUrl}
                target="_blank"
                rel="noreferrer"
                download={freieEintragungDocument.name || true}
                className="d-inline-flex align-items-center gap-1"
                style={{ color: THEME.primary }}
                onClick={(e) => e.stopPropagation()}
              >
                <FileText size={14} />
                {t("care_chat.attachment_display")} {freieEintragungDocument.name || "Dokument"}
              </a>
            ) : (
              <div
                className="d-inline-flex align-items-center gap-1"
                style={{ color: THEME.muted }}
              >
                <FileText size={14} />
                {t("care_chat.attachment_display")} {freieEintragungDocument.name || "Dokument"}
              </div>
            )}
          </div>
        )}

        {entry.tags?.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {entry.tags.map((tag) => (
              <Badge key={tag} style={TAG_BADGE_STYLE}>
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {entry.attachments?.length > 0 && (
          <div className="mt-2 small">
            {entry.attachments.map((a) => (
              <a
                key={a.url}
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="d-block"
                style={{ color: THEME.primary }}
                onClick={(e) => e.stopPropagation()}
              >
                {t("care_chat.attachment_display")} {a.name}
              </a>
            ))}
          </div>
        )}

        {showExpandToggle && (
          <button
            type="button"
            className="btn btn-link p-0 mt-1"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            style={{ color: THEME.primary }}
          >
            {isExpanded ? t("care_chat.show_less") : t("care_chat.show_more")}
          </button>
        )}

        {footer && <div className="mt-2">{footer}</div>}
      </div>

      <div className="small mt-1" style={{ color: THEME.muted }}>
        {entry.author} • {new Date(entry.timestamp).toLocaleString()}
      </div>
    </div>
  );
}

export default function TreeDetailCareChat({
  tree,
  entries,
  onEntriesChange,
  onEntryUpdate,
  onEntryDelete,
  mode,
  onPlanInspection,
  onDoInspection,
}) {
  const { t } = useTranslation();
  const [taskStatusFilter, setTaskStatusFilter] = useState("ALLE");
  const [taskPriorityFilter, setTaskPriorityFilter] = useState("ALLE");
  const [taskTypeFilter, setTaskTypeFilter] = useState("ALLE");
  const [taskSortMode, setTaskSortMode] = useState("priority");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [checklistEntryId, setChecklistEntryId] = useState(null);
  const [checklistDraft, setChecklistDraft] = useState(null);
  const [checklistMetaDraft, setChecklistMetaDraft] = useState(null);
  const [checklistError, setChecklistError] = useState(false);
  const [irrigationEntryId, setIrrigationEntryId] = useState(null);
  const [irrigationDraft, setIrrigationDraft] = useState(null);
  const [irrigationError, setIrrigationError] = useState(false);
  const [pruningEntryId, setPruningEntryId] = useState(null);
  const [pruningDraft, setPruningDraft] = useState(null);
  const [pruningError, setPruningError] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState(null);
  const [imageModalTitle, setImageModalTitle] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const now = new Date();

  const { data: usersData } = useQuery("care-users", getAllUsers, {
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const users = Array.isArray(usersData) ? usersData : [];

  function getUserLabel(u) {
    return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username || u.email || "";
  }

  const toTaskDetail = (entry) => {
    if (!entry) return null;
    if (entry.originalTask) return entry.originalTask;
    return {
      id: entry.sourceId || entry.id,
      title: entry.measureType || entry.title || "",
      description: entry.data?.beschreibung || "",
      status: entry.status,
      priority: entry.priority,
      scheduledAt: entry.timestamp,
      costCenter: entry.costCenter,
      data: entry.data,
      assignee: entry.assignee,
      tree: entry.tree,
    };
  };

  // Handler functions for three-dots menu
  const handleDeleteEntry = (id) => {
    if (onEntryDelete) {
      onEntryDelete(id);
      return;
    }
    const updated = (entries || []).filter((e) => e.id !== id);
    onEntriesChange?.(updated);
  };

  const handleEditEntry = (id) => {
    // TODO: Implement edit mode
    console.log("Edit entry:", id);
  };

  const handleToggleStatus = (entry) => {
    if (!entry) return;
    if (
      entry.measureType === "Regelkontrolle" &&
      entry.status !== "ERLEDIGT" &&
      entry.source !== "task" &&
      entry.entryKind !== "task"
    ) {
      startChecklist(entry);
      return;
    }
    if (
      mode === "tasks" &&
      isIrrigationMeasureType(entry.measureType) &&
      entry.status !== "ERLEDIGT"
    ) {
      startIrrigation(entry);
      return;
    }
    if (mode === "tasks" && entry.measureType === PRUNING_TYPE && entry.status !== "ERLEDIGT") {
      startPruning(entry);
      return;
    }
    const newStatus = entry.status === "ERLEDIGT" ? "GEPLANT" : "ERLEDIGT";
    updateEntry(entry.id, { status: newStatus });
  };

  const handleToggleFavorite = (id) => {
    const updated = (entries || []).map((e) => {
      if (e.id !== id) return e;
      return { ...e, favorite: !e.favorite };
    });
    onEntriesChange?.(updated);
  };

  const historyEntries = useMemo(() => {
    return (entries || []).filter((e) => HISTORY_STATUSES.includes(normalizeStatus(e.status)));
  }, [entries]);

  const taskEntries = useMemo(() => {
    return (entries || []).filter((e) => TASK_STATUSES.includes(normalizeStatus(e.status)));
  }, [entries]);

  const historyTypeOptions = useMemo(() => {
    const typeMap = new Map();
    historyEntries.forEach((entry) => {
      const raw = normalizeMeasureType(entry.measureType);
      if (!raw) return;
      const key = raw.toLocaleLowerCase("de");
      if (!typeMap.has(key)) typeMap.set(key, raw);
    });
    return Array.from(typeMap.values()).sort((a, b) => a.localeCompare(b, "de"));
  }, [historyEntries]);

  const taskTypeOptions = useMemo(() => {
    const typeMap = new Map();
    taskEntries.forEach((entry) => {
      const raw = normalizeMeasureType(entry.measureType);
      if (!raw) return;
      const key = raw.toLocaleLowerCase("de");
      if (!typeMap.has(key)) typeMap.set(key, raw);
    });
    return Array.from(typeMap.values()).sort((a, b) => a.localeCompare(b, "de"));
  }, [taskEntries]);

  const [historyStatusFilter, setHistoryStatusFilter] = useState("ALLE");
  const [historyPriorityFilter, setHistoryPriorityFilter] = useState("ALLE");
  const [historyTypeFilter, setHistoryTypeFilter] = useState("ALLE");
  const [historySortMode, setHistorySortMode] = useState("date");
  const [historyFiltersOpen, setHistoryFiltersOpen] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [taskFiltersOpen, setTaskFiltersOpen] = useState(false);
  const historyActiveFilterCount = [
    historyStatusFilter !== "ALLE",
    historyPriorityFilter !== "ALLE",
    historyTypeFilter !== "ALLE",
  ].filter(Boolean).length;
  const taskActiveFilterCount = [
    taskStatusFilter !== "ALLE",
    taskPriorityFilter !== "ALLE",
    taskTypeFilter !== "ALLE",
  ].filter(Boolean).length;
  const historySortLabel =
    historySortMode === "date" ? t("care_chat.sort_by_date") : t("care_chat.sort_by_priority");
  const taskSortLabel =
    taskSortMode === "priority" ? t("care_chat.sort_by_priority") : t("care_chat.sort_by_date");

  const historyFiltered = useMemo(() => {
    const filtered = historyEntries.filter((entry) => {
      if (historyStatusFilter !== "ALLE" && normalizeStatus(entry.status) !== historyStatusFilter)
        return false;
      if (
        historyPriorityFilter !== "ALLE" &&
        normalizePriority(entry.priority) !== historyPriorityFilter
      )
        return false;
      if (
        historyTypeFilter !== "ALLE" &&
        normalizeMeasureType(entry.measureType) !== historyTypeFilter
      )
        return false;
      return true;
    });

    return filtered.sort((a, b) => {
      if (historySortMode === "priority") {
        const prio =
          (PRIORITY_ORDER[normalizePriority(b.priority)] || 0) -
          (PRIORITY_ORDER[normalizePriority(a.priority)] || 0);
        if (prio !== 0) return prio;
      }
      return getTimestampMs(b.timestamp) - getTimestampMs(a.timestamp);
    });
  }, [
    historyEntries,
    historyStatusFilter,
    historyPriorityFilter,
    historyTypeFilter,
    historySortMode,
  ]);

  const tasksFiltered = useMemo(() => {
    const filtered = taskEntries.filter((entry) => {
      if (taskStatusFilter !== "ALLE" && normalizeStatus(entry.status) !== taskStatusFilter)
        return false;
      if (taskPriorityFilter !== "ALLE" && normalizePriority(entry.priority) !== taskPriorityFilter)
        return false;
      if (taskTypeFilter !== "ALLE" && normalizeMeasureType(entry.measureType) !== taskTypeFilter)
        return false;
      return true;
    });

    return filtered.sort((a, b) => {
      if (taskSortMode === "date") {
        return getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp);
      }
      const prio =
        (PRIORITY_ORDER[normalizePriority(b.priority)] || 0) -
        (PRIORITY_ORDER[normalizePriority(a.priority)] || 0);
      if (prio !== 0) return prio;
      return getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp);
    });
  }, [taskEntries, taskStatusFilter, taskPriorityFilter, taskTypeFilter, taskSortMode]);

  const nextInspection = useMemo(() => {
    const inspections = taskEntries
      .filter((e) => e.measureType === "Regelkontrolle")
      .sort((a, b) => getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp));
    return inspections.find((e) => new Date(e.timestamp) > now) || inspections[0] || null;
  }, [taskEntries, now]);

  function getCountdown(targetIso) {
    if (!targetIso) return "";
    const diffMs = new Date(targetIso) - now;
    const isOverdue = diffMs < 0;
    const absMs = Math.abs(diffMs);
    const dayMs = 1000 * 60 * 60 * 24;
    const hourMs = 1000 * 60 * 60;
    const days = isOverdue ? Math.floor(absMs / dayMs) : Math.ceil(absMs / dayMs);
    const hours = isOverdue
      ? Math.floor((absMs % dayMs) / hourMs)
      : Math.ceil((absMs % dayMs) / hourMs);
    if (days > 0) {
      return isOverdue
        ? t("care_chat.overdue_since_days", { days })
        : t("care_chat.in_days", { days });
    }
    return isOverdue
      ? t("care_chat.overdue_since_hours", { hours })
      : t("care_chat.in_hours", { hours });
  }

  function updateEntry(id, updates) {
    if (onEntryUpdate) {
      onEntryUpdate(id, updates);
      return;
    }
    onEntriesChange((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        const next = { ...e, ...updates };
        if (updates.data) {
          next.data = { ...e.data, ...updates.data };
        }
        const nextTimestamp = updates.timestamp || e.timestamp;
        next.timestamp = nextTimestamp;
        next.future = new Date(nextTimestamp) > now;
        return next;
      })
    );
  }

  function startChecklist(entry) {
    setChecklistEntryId(entry.id);
    setChecklistDraft(buildChecklistDraft(entry.data?.sichtkontrolleChecks));
    setChecklistMetaDraft(buildRegelkontrolleMeta(entry.data?.sichtkontrolleMeta, entry));
    setIrrigationEntryId(null);
    setIrrigationDraft(null);
    setPruningEntryId(null);
    setPruningDraft(null);
    setPruningError(false);
    setChecklistError(false);
  }

  function cancelChecklist() {
    setChecklistEntryId(null);
    setChecklistDraft(null);
    setChecklistMetaDraft(null);
    setChecklistError(false);
  }

  function saveChecklist(entry) {
    if (!checklistDraft || !checklistMetaDraft) return;
    if (!checklistMetaDraft.kontrolliertVon?.trim()) {
      setChecklistError(true);
      return;
    }
    const kontrolliertAmIso = toIsoFromLocal(checklistMetaDraft.kontrolliertAmLocal);
    const naechsteKontrolleIso = getNextControlIso(
      checklistMetaDraft.kontrolliertAmLocal,
      checklistMetaDraft.intervallJahre
    );
    updateEntry(entry.id, {
      status: "ERLEDIGT",
      assignedTo: checklistMetaDraft.kontrolliertVon || "",
      data: {
        sichtkontrolleChecks: checklistDraft,
        sichtkontrolleMeta: {
          kontrolliertVon: checklistMetaDraft.kontrolliertVon || "",
          kontrolliertAm: kontrolliertAmIso,
          naechsteKontrolle: naechsteKontrolleIso,
          intervallJahre: Number(checklistMetaDraft.intervallJahre || 0),
          verkehrssicher: Boolean(checklistMetaDraft.verkehrssicher),
          bemerkung: checklistMetaDraft.bemerkung || "",
        },
      },
    });
    cancelChecklist();
  }

  function startIrrigation(entry) {
    setIrrigationEntryId(entry.id);
    setIrrigationDraft(buildIrrigationDraft(entry, tree));
    setChecklistEntryId(null);
    setChecklistDraft(null);
    setChecklistMetaDraft(null);
    setEditingId(null);
    setEditDraft(null);
    setPruningEntryId(null);
    setPruningDraft(null);
    setPruningError(false);
    setIrrigationError(false);
  }

  function cancelIrrigation() {
    setIrrigationEntryId(null);
    setIrrigationDraft(null);
    setIrrigationError(false);
  }

  function saveIrrigation(entry) {
    if (!irrigationDraft) return;
    if (
      !String(irrigationDraft.waterLiters ?? "").trim() ||
      !irrigationDraft.responsiblePerson?.trim()
    ) {
      setIrrigationError(true);
      return;
    }
    const irrigationLog = {
      projectLocation: irrigationDraft.projectLocation || "",
      treeSpecies: irrigationDraft.treeSpecies || "",
      careYear: irrigationDraft.careYear || "",
      date: irrigationDraft.date || "",
      time: irrigationDraft.time || "",
      waterLiters: irrigationDraft.waterLiters ?? "",
      method: irrigationDraft.method || "",
      reason: irrigationDraft.reason || "",
      weather: irrigationDraft.weather || "",
      soilMoisture: irrigationDraft.soilMoisture || "",
      treeCondition: irrigationDraft.treeCondition || "",
      responsiblePerson: irrigationDraft.responsiblePerson || "",
      notes: irrigationDraft.notes || "",
    };
    updateEntry(entry.id, {
      status: "ERLEDIGT",
      assignedTo: irrigationLog.responsiblePerson || "",
      data: {
        irrigationLog,
        mengeLiter: irrigationLog.waterLiters,
        methode: irrigationLog.method,
        grund: irrigationLog.reason,
      },
    });
    cancelIrrigation();
  }

  function startPruning(entry) {
    setPruningEntryId(entry.id);
    setPruningDraft(buildPruningDraft(entry));
    setChecklistEntryId(null);
    setChecklistDraft(null);
    setChecklistMetaDraft(null);
    setEditingId(null);
    setEditDraft(null);
    setIrrigationEntryId(null);
    setIrrigationDraft(null);
    setPruningError(false);
  }

  function cancelPruning() {
    setPruningEntryId(null);
    setPruningDraft(null);
    setPruningError(false);
  }

  function savePruning(entry) {
    if (!pruningDraft) return;
    if (!pruningDraft.responsiblePerson?.trim()) {
      setPruningError(true);
      return;
    }
    if (!pruningDraft.type?.trim() || !pruningDraft.treeCondition?.trim()) {
      setPruningError(true);
      return;
    }
    const pruningLog = {
      type: pruningDraft.type || "",
      treeCondition: pruningDraft.treeCondition || "",
      notes: pruningDraft.notes || "",
      responsiblePerson: pruningDraft.responsiblePerson || "",
      date: pruningDraft.date || "",
      time: pruningDraft.time || "",
    };
    updateEntry(entry.id, {
      status: "ERLEDIGT",
      assignedTo: pruningLog.responsiblePerson || "",
      data: {
        pruningLog,
      },
    });
    cancelPruning();
  }

  function toggleChecklistItem(itemId) {
    setChecklistDraft((prev) => ({
      ...(prev || buildChecklistDraft()),
      [itemId]: !(prev || {})[itemId],
    }));
  }

  const nextControlLocal = checklistMetaDraft
    ? getNextControlLocal(checklistMetaDraft.kontrolliertAmLocal, checklistMetaDraft.intervallJahre)
    : "";
  const nextControlDisplay = nextControlLocal ? nextControlLocal.replace("T", " ") : "";

  function renderChecklistPanel(entry) {
    if (checklistEntryId !== entry.id || !checklistDraft || !checklistMetaDraft) return null;
    return (
      <div
        className="border rounded p-3 mt-3"
        style={{ background: THEME.chip, borderColor: THEME.border }}
      >
        <div className="small fw-semibold mb-2" style={{ color: THEME.text }}>
          {t("care_chat.inspection_title")}
        </div>
        <RegelkontrolleChecklist checks={checklistDraft} onToggle={toggleChecklistItem} />
        <div className="d-grid gap-2 mt-3">
          <div className="small fw-semibold" style={{ color: THEME.text }}>
            {t("care_chat.inspection_data")}
          </div>
          <div className="d-flex flex-wrap gap-2">
            <div className="flex-grow-1">
              <div className="small" style={{ color: THEME.muted }}>
                {t("care_chat.inspected_by")}
              </div>
              <Form.Select
                value={
                  users.find((u) => getUserLabel(u) === checklistMetaDraft?.kontrolliertVon)?.id ||
                  ""
                }
                onChange={(e) => {
                  const selected = users.find((u) => String(u.id) === e.target.value);
                  setChecklistMetaDraft((prev) => ({
                    ...prev,
                    kontrolliertVon: selected ? getUserLabel(selected) : "",
                  }));
                  if (checklistError) setChecklistError(false);
                }}
                style={INPUT_STYLE}
                isInvalid={checklistError && !checklistMetaDraft?.kontrolliertVon?.trim()}
              >
                <option value="">{t("care_chat.select_placeholder")}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {getUserLabel(u)}
                  </option>
                ))}
              </Form.Select>
              {checklistError && !checklistMetaDraft?.kontrolliertVon?.trim() && (
                <div className="small" style={{ color: THEME.danger }}>
                  {t("care_chat.please_select_person")}
                </div>
              )}
            </div>
            <div style={{ minWidth: "160px" }}>
              <div className="small" style={{ color: THEME.muted }}>
                {t("care_chat.interval_years")}
              </div>
              <Form.Control
                type="number"
                min="1"
                step="1"
                value={checklistMetaDraft?.intervallJahre ?? ""}
                onChange={(e) =>
                  setChecklistMetaDraft((prev) => ({
                    ...prev,
                    intervallJahre: Number(e.target.value),
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
          </div>
          <div
            className="gap-2"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            }}
          >
            <div className="d-flex flex-column gap-1">
              <div className="small" style={{ color: THEME.muted }}>
                {t("care_chat.date")}
              </div>
              <Form.Control
                type="date"
                value={
                  checklistMetaDraft?.kontrolliertAmLocal
                    ? checklistMetaDraft.kontrolliertAmLocal.slice(0, 10)
                    : ""
                }
                onChange={(e) => {
                  const currentLocal =
                    checklistMetaDraft?.kontrolliertAmLocal || toLocalDatetimeValue(new Date());
                  const nextTimestamp = e.target.value + currentLocal.slice(10);
                  setChecklistMetaDraft((prev) => ({
                    ...prev,
                    kontrolliertAmLocal: nextTimestamp,
                  }));
                }}
                style={INPUT_STYLE}
              />
            </div>
            <div className="d-flex flex-column gap-1">
              <div className="small" style={{ color: THEME.muted }}>
                {t("care_chat.time")}
              </div>
              <Form.Control
                type="time"
                value={
                  checklistMetaDraft?.kontrolliertAmLocal
                    ? checklistMetaDraft.kontrolliertAmLocal.slice(11, 16)
                    : ""
                }
                onChange={(e) => {
                  const currentLocal =
                    checklistMetaDraft?.kontrolliertAmLocal || toLocalDatetimeValue(new Date());
                  const nextTimestamp = currentLocal.slice(0, 11) + e.target.value;
                  setChecklistMetaDraft((prev) => ({
                    ...prev,
                    kontrolliertAmLocal: nextTimestamp,
                  }));
                }}
                style={INPUT_STYLE}
              />
            </div>
            <div className="d-flex flex-column gap-1">
              <div className="small" style={{ color: THEME.muted }}>
                {t("care_chat.next_inspection")}
              </div>
              <Form.Control value={nextControlDisplay} readOnly style={INPUT_STYLE} />
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.road_safe")}
            </div>
            <Form.Check
              inline
              type="radio"
              label={t("care_chat.yes")}
              name={`verkehrssicher-${entry.id}`}
              checked={checklistMetaDraft?.verkehrssicher === true}
              onChange={() => setChecklistMetaDraft((prev) => ({ ...prev, verkehrssicher: true }))}
            />
            <Form.Check
              inline
              type="radio"
              label={t("care_chat.no")}
              name={`verkehrssicher-${entry.id}`}
              checked={checklistMetaDraft?.verkehrssicher === false}
              onChange={() => setChecklistMetaDraft((prev) => ({ ...prev, verkehrssicher: false }))}
            />
          </div>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder={t("care_chat.remark_optional")}
            value={checklistMetaDraft?.bemerkung || ""}
            onChange={(e) =>
              setChecklistMetaDraft((prev) => ({ ...prev, bemerkung: e.target.value }))
            }
            style={INPUT_STYLE}
          />
        </div>
        <div className="d-flex gap-2 mt-3">
          <Button
            size="sm"
            onClick={() => saveChecklist(entry)}
            style={{ background: THEME.primary, borderColor: THEME.primary, color: "#ffffff" }}
          >
            {t("care_chat.save")}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={cancelChecklist}
            style={{ borderColor: THEME.primary, color: THEME.primary }}
          >
            {t("care_chat.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  function renderIrrigationPanel(entry) {
    if (irrigationEntryId !== entry.id || !irrigationDraft) return null;
    return (
      <div
        className="border rounded p-3 mt-3"
        style={{ background: THEME.chip, borderColor: THEME.border }}
      >
        <div className="small fw-semibold mb-2" style={{ color: THEME.text }}>
          {t("care_chat.irrigation_protocol_title")}
        </div>
        <div
          className="gap-2 mb-2"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
        >
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.date")}
            </div>
            <Form.Control
              type="date"
              value={irrigationDraft.date || ""}
              onChange={(e) => setIrrigationDraft((prev) => ({ ...prev, date: e.target.value }))}
              style={INPUT_STYLE}
            />
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.time")}
            </div>
            <Form.Control
              type="time"
              value={irrigationDraft.time || ""}
              onChange={(e) => setIrrigationDraft((prev) => ({ ...prev, time: e.target.value }))}
              style={INPUT_STYLE}
            />
          </div>
        </div>
        <div
          className="gap-2 mt-2"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
        >
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.water_amount_liters")} <span style={{ color: THEME.danger }}>*</span>
            </div>
            <Form.Control
              type="number"
              value={irrigationDraft.waterLiters}
              onChange={(e) => {
                setIrrigationDraft((prev) => ({ ...prev, waterLiters: e.target.value }));
                if (irrigationError) setIrrigationError(false);
              }}
              style={INPUT_STYLE}
              isInvalid={irrigationError && !String(irrigationDraft.waterLiters ?? "").trim()}
            />
            {irrigationError && !String(irrigationDraft.waterLiters ?? "").trim() && (
              <div className="small" style={{ color: THEME.danger }}>
                {t("care_chat.water_amount_required")}
              </div>
            )}
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.irrigation_method")}
            </div>
            <Form.Control
              value={irrigationDraft.method}
              onChange={(e) => setIrrigationDraft((prev) => ({ ...prev, method: e.target.value }))}
              placeholder={t("care_chat.irrigation_method_placeholder")}
              style={INPUT_STYLE}
            />
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.reason")}
            </div>
            <Form.Control
              value={irrigationDraft.reason}
              onChange={(e) => setIrrigationDraft((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder={t("care_chat.reason_placeholder")}
              style={INPUT_STYLE}
            />
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.weather_conditions")}
            </div>
            <Form.Select
              value={irrigationDraft.weather}
              onChange={(e) => setIrrigationDraft((prev) => ({ ...prev, weather: e.target.value }))}
              style={INPUT_STYLE}
            >
              <option value="">{t("care_chat.select_placeholder")}</option>
              {IRRIGATION_WEATHER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.soil_moisture")}
            </div>
            <Form.Select
              value={irrigationDraft.soilMoisture}
              onChange={(e) =>
                setIrrigationDraft((prev) => ({ ...prev, soilMoisture: e.target.value }))
              }
              style={INPUT_STYLE}
            >
              <option value="">{t("care_chat.select_placeholder")}</option>
              {IRRIGATION_SOIL_MOISTURE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.tree_condition")}
            </div>
            <Form.Select
              value={irrigationDraft.treeCondition}
              onChange={(e) =>
                setIrrigationDraft((prev) => ({ ...prev, treeCondition: e.target.value }))
              }
              style={INPUT_STYLE}
            >
              <option value="">{t("care_chat.select_placeholder")}</option>
              {IRRIGATION_TREE_CONDITION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.responsible_person")}
            </div>
            <Form.Select
              value={
                users.find((u) => getUserLabel(u) === irrigationDraft.responsiblePerson)?.id || ""
              }
              onChange={(e) => {
                const selected = users.find((u) => String(u.id) === e.target.value);
                setIrrigationDraft((prev) => ({
                  ...prev,
                  responsiblePerson: selected ? getUserLabel(selected) : "",
                }));
                if (irrigationError) setIrrigationError(false);
              }}
              style={INPUT_STYLE}
              isInvalid={irrigationError && !irrigationDraft.responsiblePerson?.trim()}
            >
              <option value="">{t("care_chat.select_placeholder")}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {getUserLabel(u)}
                </option>
              ))}
            </Form.Select>
            {irrigationError && !irrigationDraft.responsiblePerson?.trim() && (
              <div className="small" style={{ color: THEME.danger }}>
                {t("care_chat.please_select_person")}
              </div>
            )}
          </div>
        </div>
        <div className="mt-2">
          <div className="small" style={{ color: THEME.muted }}>
            {t("care_chat.remarks")}
          </div>
          <Form.Control
            as="textarea"
            rows={2}
            value={irrigationDraft.notes}
            onChange={(e) => setIrrigationDraft((prev) => ({ ...prev, notes: e.target.value }))}
            style={INPUT_STYLE}
          />
        </div>
        <div className="d-flex gap-2 mt-3">
          <Button
            size="sm"
            onClick={() => saveIrrigation(entry)}
            style={{ background: THEME.primary, borderColor: THEME.primary, color: "#ffffff" }}
          >
            {t("care_chat.save")}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={cancelIrrigation}
            style={{ borderColor: THEME.primary, color: THEME.primary }}
          >
            {t("care_chat.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  function renderPruningPanel(entry) {
    if (pruningEntryId !== entry.id || !pruningDraft) return null;
    return (
      <div
        className="border rounded p-3 mt-3"
        style={{ background: THEME.chip, borderColor: THEME.border }}
      >
        <div className="small fw-semibold mb-2" style={{ color: THEME.text }}>
          {t("care_chat.pruning_protocol_title")}
        </div>
        <div
          className="gap-2 mb-2"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
        >
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.date")}
            </div>
            <Form.Control
              type="date"
              value={pruningDraft.date || ""}
              onChange={(e) => setPruningDraft((prev) => ({ ...prev, date: e.target.value }))}
              style={INPUT_STYLE}
            />
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="small" style={{ color: THEME.muted }}>
              {t("care_chat.time")}
            </div>
            <Form.Control
              type="time"
              value={pruningDraft.time || ""}
              onChange={(e) => setPruningDraft((prev) => ({ ...prev, time: e.target.value }))}
              style={INPUT_STYLE}
            />
          </div>
        </div>
        <div className="d-flex flex-column gap-1">
          <div className="small" style={{ color: THEME.muted }}>
            {t("care_chat.pruning_type")} <span style={{ color: THEME.danger }}>*</span>
          </div>
          <Form.Select
            value={pruningDraft.type}
            onChange={(e) => {
              setPruningDraft((prev) => ({ ...prev, type: e.target.value }));
              if (pruningError) setPruningError(false);
            }}
            style={INPUT_STYLE}
            isInvalid={pruningError && !pruningDraft.type?.trim()}
          >
            <option value="">{t("care_chat.select_placeholder")}</option>
            {PRUNING_CATEGORIES.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </Form.Select>
          {pruningError && !pruningDraft.type?.trim() && (
            <div className="small" style={{ color: THEME.danger }}>
              {t("care_chat.please_provide_type")}
            </div>
          )}
        </div>
        <div className="d-flex flex-column gap-1 mt-2">
          <div className="small" style={{ color: THEME.muted }}>
            {t("care_chat.tree_condition")} <span style={{ color: THEME.danger }}>*</span>
          </div>
          <Form.Control
            value={pruningDraft.treeCondition}
            onChange={(e) => {
              setPruningDraft((prev) => ({ ...prev, treeCondition: e.target.value }));
              if (pruningError) setPruningError(false);
            }}
            placeholder={t("care_chat.tree_condition_placeholder")}
            style={INPUT_STYLE}
            isInvalid={pruningError && !pruningDraft.treeCondition?.trim()}
          />
          {pruningError && !pruningDraft.treeCondition?.trim() && (
            <div className="small" style={{ color: THEME.danger }}>
              {t("care_chat.please_provide_tree_condition")}
            </div>
          )}
        </div>
        <div className="d-flex flex-column gap-1 mt-2">
          <div className="small" style={{ color: THEME.muted }}>
            {t("care_chat.responsible_person")} <span style={{ color: THEME.danger }}>*</span>
          </div>
          <Form.Select
            value={users.find((u) => getUserLabel(u) === pruningDraft.responsiblePerson)?.id || ""}
            onChange={(e) => {
              const selected = users.find((u) => String(u.id) === e.target.value);
              setPruningDraft((prev) => ({
                ...prev,
                responsiblePerson: selected ? getUserLabel(selected) : "",
              }));
              if (pruningError) setPruningError(false);
            }}
            style={INPUT_STYLE}
            isInvalid={pruningError && !pruningDraft.responsiblePerson?.trim()}
          >
            <option value="">{t("care_chat.select_placeholder")}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {getUserLabel(u)}
              </option>
            ))}
          </Form.Select>
          {pruningError && !pruningDraft.responsiblePerson?.trim() && (
            <div className="small" style={{ color: THEME.danger }}>
              {t("care_chat.please_select_person")}
            </div>
          )}
        </div>
        <div className="mt-2">
          <div className="small" style={{ color: THEME.muted }}>
            {t("care_chat.remarks")}
          </div>
          <Form.Control
            as="textarea"
            rows={2}
            value={pruningDraft.notes}
            onChange={(e) => setPruningDraft((prev) => ({ ...prev, notes: e.target.value }))}
            style={INPUT_STYLE}
          />
        </div>
        <div className="d-flex gap-2 mt-3">
          <Button
            size="sm"
            onClick={() => savePruning(entry)}
            style={{ background: THEME.primary, borderColor: THEME.primary, color: "#ffffff" }}
          >
            {t("care_chat.save")}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={cancelPruning}
            style={{ borderColor: THEME.primary, color: THEME.primary }}
          >
            {t("care_chat.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  function startEdit(entry) {
    setEditingId(entry.id);
    setEditDraft({
      timestampLocal: toLocalDatetimeValue(entry.timestamp),
      priority: normalizePriority(entry.priority) || "MITTEL",
      status: normalizeStatus(entry.status) || "GEPLANT",
      beschreibung: entry.data?.beschreibung || "",
      costCenter: entry.costCenter || "",
      assignedTo: entry.assignedTo || "",
    });
    setChecklistEntryId(null);
    setChecklistDraft(null);
    setChecklistMetaDraft(null);
    setIrrigationEntryId(null);
    setIrrigationDraft(null);
    setPruningEntryId(null);
    setPruningDraft(null);
    setPruningError(false);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft(null);
  }

  function saveEdit(id) {
    if (!editDraft?.timestampLocal) return;
    const nextTimestamp = new Date(editDraft.timestampLocal).toISOString();
    const isPast = new Date(nextTimestamp) <= now;
    let nextStatus = editDraft.status || "GEPLANT";
    if (isPast && TASK_STATUSES.includes(nextStatus)) {
      nextStatus = "ERLEDIGT";
    }
    updateEntry(id, {
      timestamp: nextTimestamp,
      priority: editDraft.priority,
      status: nextStatus,
      costCenter: editDraft.costCenter,
      assignedTo: editDraft.assignedTo,
      data: { beschreibung: editDraft.beschreibung },
    });
    cancelEdit();
  }

  if (mode === "history") {
    return (
      <>
        <style>{`
          .custom-dropdown::after {
            display: none !important;
          }
          .custom-dropdown:hover {
            background-color: ${THEME.chip} !important;
          }
          .dropdown-item:hover {
            background-color: ${THEME.chip} !important;
            color: ${THEME.text} !important;
          }
        `}</style>
        <div>
          <div className="mb-2 p-2 rounded" style={FILTER_BAR_STYLE}>
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div className="d-flex align-items-center gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setHistoryFiltersOpen((prev) => !prev)}
                  style={getToolIconButtonStyle(historyFiltersOpen)}
                  title={t("filter")}
                >
                  <FilterIconSolid />
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    setHistorySortMode((prev) => (prev === "date" ? "priority" : "date"))
                  }
                  style={getToolIconButtonStyle()}
                  title={historySortLabel}
                >
                  {historySortMode === "date" ? <SortDownIcon /> : <SortUpIcon />}
                </Button>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="small" style={{ color: THEME.muted }}>
                  {historySortLabel}
                </span>
                {historyActiveFilterCount > 0 && (
                  <Badge bg="light" text="dark" className="border" style={{ fontWeight: 500 }}>
                    {historyActiveFilterCount}
                  </Badge>
                )}
              </div>
            </div>
            {historyFiltersOpen && (
              <>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  <Dropdown>
                    <Dropdown.Toggle
                      size="sm"
                      variant="outline-secondary"
                      style={getToolIconButtonStyle(historyStatusFilter !== "ALLE")}
                      className="custom-dropdown"
                      title={t("care_chat.all_statuses")}
                    >
                      <Check size={14} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setHistoryStatusFilter("ALLE")}>
                        {t("care_chat.all_statuses")}
                      </Dropdown.Item>
                      {HISTORY_STATUSES.map((status) => (
                        <Dropdown.Item key={status} onClick={() => setHistoryStatusFilter(status)}>
                          {t(STATUS_LABEL_KEYS[status] || status)}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown>
                    <Dropdown.Toggle
                      size="sm"
                      variant="outline-secondary"
                      style={getToolIconButtonStyle(historyPriorityFilter !== "ALLE")}
                      className="custom-dropdown"
                      title={t("care_chat.all_priorities")}
                    >
                      <Star size={14} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setHistoryPriorityFilter("ALLE")}>
                        {t("care_chat.all_priorities")}
                      </Dropdown.Item>
                      {PRIORITY_FILTER_VALUES.map((priority) => (
                        <Dropdown.Item
                          key={priority}
                          onClick={() => setHistoryPriorityFilter(priority)}
                        >
                          {t(PRIORITY_LABEL_KEYS[priority])}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown>
                    <Dropdown.Toggle
                      size="sm"
                      variant="outline-secondary"
                      style={getToolIconButtonStyle(historyTypeFilter !== "ALLE")}
                      className="custom-dropdown"
                      title={t("care_chat.all_types")}
                    >
                      <FileText size={14} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setHistoryTypeFilter("ALLE")}>
                        {t("care_chat.all_types")}
                      </Dropdown.Item>
                      {historyTypeOptions.map((type) => (
                        <Dropdown.Item key={type} onClick={() => setHistoryTypeFilter(type)}>
                          {MEASURE_TYPE_LABEL_KEYS[type] ? t(MEASURE_TYPE_LABEL_KEYS[type]) : type}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  {historyStatusFilter !== "ALLE" && (
                    <Badge bg="light" text="dark" className="border d-flex align-items-center">
                      {t(STATUS_LABEL_KEYS[historyStatusFilter] || historyStatusFilter)}
                    </Badge>
                  )}
                  {historyPriorityFilter !== "ALLE" && (
                    <Badge bg="light" text="dark" className="border d-flex align-items-center">
                      {t(PRIORITY_LABEL_KEYS[historyPriorityFilter] || historyPriorityFilter)}
                    </Badge>
                  )}
                  {historyTypeFilter !== "ALLE" && (
                    <Badge bg="light" text="dark" className="border d-flex align-items-center">
                      {MEASURE_TYPE_LABEL_KEYS[historyTypeFilter]
                        ? t(MEASURE_TYPE_LABEL_KEYS[historyTypeFilter])
                        : historyTypeFilter}
                    </Badge>
                  )}
                </div>
              </>
            )}
          </div>

          {historyFiltered.length === 0 && (
            <div className="text-muted">{t("care_chat.no_entries_found")}</div>
          )}
          {historyFiltered.map((entry) => (
            <ChatBubble
              key={entry.id}
              entry={entry}
              align="start"
              anchorId={`care-entry-${entry.id}`}
              onDelete={handleDeleteEntry}
              onEdit={handleEditEntry}
              onToggleStatus={handleToggleStatus}
              onToggleFavorite={handleToggleFavorite}
              onImageClick={(url, title) => {
                setImageModalUrl(url);
                setImageModalTitle(title);
              }}
              onTaskClick={(entry) => {
                if (!entry) return;
                const taskDetail = toTaskDetail(entry);
                if (!taskDetail) return;
                setSelectedTask(taskDetail);
                setShowTaskDetail(true);
              }}
              isHistory
              isExpanded={Boolean(expandedHistory[entry.id])}
              onToggleExpand={() =>
                setExpandedHistory((prev) => ({
                  ...prev,
                  [entry.id]: !prev[entry.id],
                }))
              }
              footer={
                entry.source === "image" ? null : (
                  <div className="d-flex flex-wrap gap-2">
                    <Badge style={getStatusBadgeStyle(entry.status)}>
                      {STATUS_LABEL_KEYS[entry.status]
                        ? t(STATUS_LABEL_KEYS[entry.status])
                        : entry.status}
                    </Badge>
                    {entry.priority && (
                      <Badge style={getPriorityBadgeStyle(entry.priority)}>
                        {PRIORITY_LABEL_KEYS[entry.priority]
                          ? t(PRIORITY_LABEL_KEYS[entry.priority])
                          : entry.priority}
                      </Badge>
                    )}
                  </div>
                )
              }
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        .custom-dropdown::after {
          display: none !important;
        }
        .custom-dropdown:hover {
          background-color: ${THEME.chip} !important;
        }
        .dropdown-item:hover {
          background-color: ${THEME.chip} !important;
          color: ${THEME.text} !important;
        }
      `}</style>
      <div>
        {/* Temporarily hidden per UX request:
        <div
          className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 px-2 py-2 rounded"
          style={CONTROL_BAR_STYLE}
        >
          <div className="small" style={{ color: THEME.muted }}>
            <span className="fw-semibold" style={{ color: THEME.primary }}>
              {t("care_chat.next_inspection_label")}
            </span>{" "}
            {nextInspection ? (
              <>
                {new Date(nextInspection.timestamp).toLocaleString()} •{" "}
                {getCountdown(nextInspection.timestamp)}
              </>
            ) : (
              t("care_chat.no_inspection_planned")
            )}
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              style={{ borderColor: THEME.primary, color: THEME.primary }}
              onClick={() => onPlanInspection?.()}
            >
              {t("care_chat.plan_inspection")}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              style={{ borderColor: THEME.primary, color: THEME.primary }}
              onClick={() => onDoInspection?.()}
            >
              {t("care_chat.perform_inspection")}
            </Button>
          </div>
        </div>
        */}

        <div className="mb-2 p-2 rounded" style={FILTER_BAR_STYLE}>
          <div className="d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setTaskFiltersOpen((prev) => !prev)}
                style={getToolIconButtonStyle(taskFiltersOpen)}
                title={t("filter")}
              >
                <FilterIconSolid />
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() =>
                  setTaskSortMode((prev) => (prev === "priority" ? "date" : "priority"))
                }
                style={getToolIconButtonStyle()}
                title={taskSortLabel}
              >
                {taskSortMode === "priority" ? <SortUpIcon /> : <SortDownIcon />}
              </Button>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="small" style={{ color: THEME.muted }}>
                {taskSortLabel}
              </span>
              {taskActiveFilterCount > 0 && (
                <Badge bg="light" text="dark" className="border" style={{ fontWeight: 500 }}>
                  {taskActiveFilterCount}
                </Badge>
              )}
            </div>
          </div>
          {taskFiltersOpen && (
            <>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Dropdown>
                  <Dropdown.Toggle
                    size="sm"
                    variant="outline-secondary"
                    style={getToolIconButtonStyle(taskStatusFilter !== "ALLE")}
                    className="custom-dropdown"
                    title={t("care_chat.all_statuses")}
                  >
                    <Check size={14} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setTaskStatusFilter("ALLE")}>
                      {t("care_chat.all_statuses")}
                    </Dropdown.Item>
                    {TASK_STATUSES.map((status) => (
                      <Dropdown.Item key={status} onClick={() => setTaskStatusFilter(status)}>
                        {t(STATUS_LABEL_KEYS[status] || status)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown>
                  <Dropdown.Toggle
                    size="sm"
                    variant="outline-secondary"
                    style={getToolIconButtonStyle(taskPriorityFilter !== "ALLE")}
                    className="custom-dropdown"
                    title={t("care_chat.all_priorities")}
                  >
                    <Star size={14} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setTaskPriorityFilter("ALLE")}>
                      {t("care_chat.all_priorities")}
                    </Dropdown.Item>
                    {PRIORITY_FILTER_VALUES.map((priority) => (
                      <Dropdown.Item key={priority} onClick={() => setTaskPriorityFilter(priority)}>
                        {t(PRIORITY_LABEL_KEYS[priority])}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown>
                  <Dropdown.Toggle
                    size="sm"
                    variant="outline-secondary"
                    style={getToolIconButtonStyle(taskTypeFilter !== "ALLE")}
                    className="custom-dropdown"
                    title={t("care_chat.all_types")}
                  >
                    <FileText size={14} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setTaskTypeFilter("ALLE")}>
                      {t("care_chat.all_types")}
                    </Dropdown.Item>
                    {taskTypeOptions.map((type) => (
                      <Dropdown.Item key={type} onClick={() => setTaskTypeFilter(type)}>
                        {MEASURE_TYPE_LABEL_KEYS[type] ? t(MEASURE_TYPE_LABEL_KEYS[type]) : type}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {taskStatusFilter !== "ALLE" && (
                  <Badge bg="light" text="dark" className="border d-flex align-items-center">
                    {t(STATUS_LABEL_KEYS[taskStatusFilter] || taskStatusFilter)}
                  </Badge>
                )}
                {taskPriorityFilter !== "ALLE" && (
                  <Badge bg="light" text="dark" className="border d-flex align-items-center">
                    {t(PRIORITY_LABEL_KEYS[taskPriorityFilter] || taskPriorityFilter)}
                  </Badge>
                )}
                {taskTypeFilter !== "ALLE" && (
                  <Badge bg="light" text="dark" className="border d-flex align-items-center">
                    {MEASURE_TYPE_LABEL_KEYS[taskTypeFilter]
                      ? t(MEASURE_TYPE_LABEL_KEYS[taskTypeFilter])
                      : taskTypeFilter}
                  </Badge>
                )}
              </div>
            </>
          )}
        </div>

        {tasksFiltered.length === 0 && (
          <div className="text-muted">{t("care_chat.no_tasks_found")}</div>
        )}

        {tasksFiltered.map((entry) => {
          const overdue = new Date(entry.timestamp) < now;
          const highlight = overdue;

          const footer = (
            <>
              <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                <div className="d-flex flex-wrap gap-2">
                  {entry.status && (
                    <Badge style={getStatusBadgeStyle(entry.status)}>
                      {STATUS_LABEL_KEYS[entry.status]
                        ? t(STATUS_LABEL_KEYS[entry.status])
                        : entry.status}
                    </Badge>
                  )}
                  {entry.priority && (
                    <Badge style={getPriorityBadgeStyle(entry.priority)}>
                      {PRIORITY_LABEL_KEYS[entry.priority]
                        ? t(PRIORITY_LABEL_KEYS[entry.priority])
                        : entry.priority}
                    </Badge>
                  )}
                  {entry.costCenter && (
                    <Badge style={META_BADGE_STYLE}>
                      {t("care_chat.cost_center_display")} {entry.costCenter}
                    </Badge>
                  )}
                  {entry.assignedTo && (
                    <Badge style={META_BADGE_STYLE}>
                      {t("care_chat.assignment_display")} {entry.assignedTo}
                    </Badge>
                  )}
                  <Badge style={TERM_BADGE_STYLE}>
                    {t("care_chat.appointment_label")} {new Date(entry.timestamp).toLocaleString()}
                  </Badge>
                </div>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => startEdit(entry)}
                  style={{
                    background: "#ffffff",
                    border: `1px solid ${THEME.border}`,
                    width: "30px",
                    height: "30px",
                    padding: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "6px",
                  }}
                  title={t("care_chat.edit")}
                >
                  <EditIcon size="22px" color={THEME.muted} />
                </button>
              </div>

              <div className="d-flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={() => {
                    if (entry.measureType === "Regelkontrolle") {
                      startChecklist(entry);
                      return;
                    }
                    if (isIrrigationMeasureType(entry.measureType)) {
                      startIrrigation(entry);
                      return;
                    }
                    if (entry.measureType === PRUNING_TYPE) {
                      startPruning(entry);
                      return;
                    }
                    updateEntry(entry.id, { status: "ERLEDIGT" });
                  }}
                  style={{ borderColor: THEME.success, color: THEME.success }}
                >
                  {t("care_chat.mark_done")}
                </Button>
              </div>

              {renderChecklistPanel(entry)}
              {renderIrrigationPanel(entry)}
              {renderPruningPanel(entry)}

              {editingId === entry.id && editDraft && (
                <div
                  className="border rounded p-3 mt-3"
                  style={{ background: THEME.chip, borderColor: THEME.border }}
                >
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Form.Control
                      type="datetime-local"
                      value={editDraft.timestampLocal}
                      onChange={(e) => {
                        const nextTimestamp = e.target.value;
                        setEditDraft((prev) => {
                          const options = getStatusOptionsForTimestamp(nextTimestamp, now);
                          const nextStatus = options.includes(prev.status)
                            ? prev.status
                            : options[0];
                          return { ...prev, timestampLocal: nextTimestamp, status: nextStatus };
                        });
                      }}
                      className="flex-grow-1"
                    />
                    <Form.Select
                      value={editDraft.status}
                      onChange={(e) =>
                        setEditDraft((prev) => ({ ...prev, status: e.target.value }))
                      }
                      className="flex-grow-1"
                    >
                      {getStatusOptionsForTimestamp(editDraft.timestampLocal, now).map((status) => (
                        <option key={status} value={status}>
                          {t(STATUS_LABEL_KEYS[status] || status)}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      value={editDraft.priority}
                      onChange={(e) =>
                        setEditDraft((prev) => ({ ...prev, priority: e.target.value }))
                      }
                      className="flex-grow-1"
                    >
                      {PRIORITY_FILTER_VALUES.map((priority) => (
                        <option key={priority} value={priority}>
                          {t(PRIORITY_LABEL_KEYS[priority])}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="mb-2"
                    placeholder={t("care_chat.description")}
                    value={editDraft.beschreibung}
                    onChange={(e) =>
                      setEditDraft((prev) => ({ ...prev, beschreibung: e.target.value }))
                    }
                  />
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Form.Control
                      placeholder={t("care_chat.cost_center")}
                      value={editDraft.costCenter}
                      onChange={(e) =>
                        setEditDraft((prev) => ({ ...prev, costCenter: e.target.value }))
                      }
                      className="flex-grow-1"
                    />
                    <Form.Select
                      value={users.find((u) => getUserLabel(u) === editDraft.assignedTo)?.id || ""}
                      onChange={(e) => {
                        const selected = users.find((u) => String(u.id) === e.target.value);
                        setEditDraft((prev) => ({
                          ...prev,
                          assignedTo: selected ? getUserLabel(selected) : "",
                        }));
                      }}
                      className="flex-grow-1"
                    >
                      <option value="">{t("care_chat.assignment")}</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {getUserLabel(u)}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => saveEdit(entry.id)}
                      style={{
                        background: THEME.primary,
                        borderColor: THEME.primary,
                        color: "#ffffff",
                      }}
                    >
                      {t("care_chat.save")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={cancelEdit}
                      style={{ borderColor: THEME.primary, color: THEME.primary }}
                    >
                      {t("care_chat.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </>
          );

          return (
            <ChatBubble
              key={entry.id}
              entry={entry}
              align="end"
              highlight={highlight}
              footer={footer}
              anchorId={`care-entry-${entry.id}`}
              onDelete={handleDeleteEntry}
              onEdit={handleEditEntry}
              onToggleStatus={handleToggleStatus}
              onToggleFavorite={handleToggleFavorite}
              onImageClick={(url, title) => {
                setImageModalUrl(url);
                setImageModalTitle(title);
              }}
              onTaskClick={(entry) => {
                if (!entry) return;
                const taskDetail = toTaskDetail(entry);
                if (!taskDetail) return;
                setSelectedTask(taskDetail);
                setShowTaskDetail(true);
              }}
            />
          );
        })}
      </div>

      {/* Image Modal */}
      <Modal show={Boolean(imageModalUrl)} onHide={() => setImageModalUrl(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{imageModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <img
            src={imageModalUrl}
            alt={imageModalTitle}
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </Modal.Body>
      </Modal>

      {/* Task Detail */}
      <Modal
        show={showTaskDetail}
        onHide={() => {
          setShowTaskDetail(false);
          setSelectedTask(null);
        }}
        centered
        size="lg"
      >
        <Modal.Body className="p-0" style={{ maxHeight: "80vh", overflow: "auto" }}>
          {selectedTask && (
            <TaskDetail
              task={selectedTask}
              showEditButton={false}
              onClose={() => {
                setShowTaskDetail(false);
                setSelectedTask(null);
              }}
              onDelete={() => {
                if (onEntryDelete && selectedTask) {
                  onEntryDelete(`task-${selectedTask.id}`);
                }
                setShowTaskDetail(false);
                setSelectedTask(null);
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
